/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useProfileSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling AI profile form submission and deletion.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createAiProfile, updateAiProfile, deleteAiProfile } from '../api/aiProfilesApi';
import type { AiProfile } from '../api/aiProfilesApi';

type FormData = Partial<AiProfile> & Pick<AiProfile, 'domain_id' | 'type' | 'model' | 'status'>;

export function useProfileSubmit(
  formData: FormData,
  baseline: FormData | null,
  hasChanges: boolean,
  validateForm: () => boolean,
  mode: 'create' | 'edit',
  id: string | undefined,
  setFormData: (data: FormData | ((prev: FormData) => FormData)) => void,
  setBaseline: (data: FormData | null) => void
) {
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const translateRef = useRef(t);
  const showSuccessRef = useRef(showSuccess);
  const showErrorRef = useRef(showError);
  const showInfoRef = useRef(showInfo);

  useEffect(() => {
    translateRef.current = t;
    showSuccessRef.current = showSuccess;
    showErrorRef.current = showError;
    showInfoRef.current = showInfo;
  }, [t, showSuccess, showError, showInfo]);

  const submitProfile = useCallback(
    async (): Promise<AiProfile | undefined> => {
      const translate = translateRef.current;

      if (!validateForm()) {
        showErrorRef.current?.(translate('aiProfiles.messages.validationError'));
        return undefined;
      }

      setIsSaving(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = { ...formData };

        // Convert voice_config to string if it's an object
        if (payload.voice_config && typeof payload.voice_config === 'object') {
          payload.voice_config = JSON.stringify(payload.voice_config);
        }

        // Handle null values
        if (payload.temperature === undefined) payload.temperature = null;
        if (payload.max_tokens === undefined) payload.max_tokens = null;
        if (payload.timeout_ms === undefined) payload.timeout_ms = null;
        if (payload.retry_attempts === undefined) payload.retry_attempts = null;
        if (!payload.embeddings_model) payload.embeddings_model = null;

        let result;
        if (mode === 'edit' && id && id !== 'new') {
          result = await updateAiProfile(id, payload);
        } else {
          result = await createAiProfile(payload);
        }

        showSuccessRef.current?.(
          mode === 'edit'
            ? translate('aiProfiles.messages.updateSuccess')
            : translate('aiProfiles.messages.createSuccess')
        );

        // Update baseline after successful save
        if (result.data) {
          setFormData(result.data);
          setBaseline(result.data);
          // Return the saved profile data
          return result.data as AiProfile;
        }

        return undefined;
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : translate('aiProfiles.messages.saveError');
        showErrorRef.current?.(message);
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [formData, baseline, hasChanges, validateForm, mode, id, setFormData, setBaseline]
  );

  // Handle form submit (for backward compatibility)
  const handleFormSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault();
      await submitProfile();
    },
    [submitProfile]
  );

  const handleCancel = useCallback(() => {
    if (mode === 'edit' && baseline) {
      // Restore original form data
      setFormData({ ...baseline });
    } else {
      // Reset to defaults in create mode
      setFormData({
        domain_id: 1,
        provider_id: undefined,
        type: 'general',
        model: '',
        temperature: 0.7,
        max_tokens: 1000,
        voice_model: '',
        embeddings_model: '',
        timeout_ms: 30000,
        retry_attempts: 3,
        status: true
      } as Partial<AiProfile> & Pick<AiProfile, 'domain_id' | 'type' | 'model' | 'status'>);
    }
  }, [mode, baseline, setFormData]);

  const handleDelete = useCallback(async () => {
    if (!id || id === 'new') return;

    const translate = translateRef.current;
    setIsDeleting(true);
    try {
      await deleteAiProfile(id);
      showSuccessRef.current?.(translate('aiProfiles.messages.deleteSuccess'));
      setShowDeleteModal(false);
      // Navigation will be handled by the form view
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : translate('aiProfiles.messages.deleteError');
      showErrorRef.current?.(message);
    } finally {
      setIsDeleting(false);
    }
  }, [id]);

  return {
    isSaving,
    isDeleting,
    showDeleteModal,
    setShowDeleteModal,
    handleFormSubmit,
    submitProfile,
    handleCancel,
    handleDelete
  };
}

